import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { v4 } from "uuid";

import type { ShareableIncidentDataV1Response } from "@thalamos/common";
import { OfficerRequestStatusSchema } from "@thalamos/common";
import { triggerDownload } from "@thalamos/frontend";
import type {
  ExternalPatientLinkEnhanced,
  FormContextData,
  PatientTimelineResponse,
} from "@thalamos/aspire-common";
import { buildDefaultContext, formIsCompleted } from "@thalamos/aspire-common";

import { FormContextPdfViewer } from "../helpers/FormContextPdfViewer.js";
import { ParticipantsList } from "../helpers/ParticipantsList.js";
import { WorkItemStatusCard } from "../helpers/WorkItemStatusCard.js";
import { canReconcile as checkCanReconcile } from "./canReconcile.js";
import { generateMenuOptions } from "./helpers.js";
import {
  buildDetentionStartPrefill,
  getIncidentTranslationContext,
  policeIncidentLogic,
  resolveEmhaParticipant,
} from "./logic.js";
import { useOutcomeFormInProgressCards } from "./OutcomeFormSection.js";
import { RenderOfficerStatus } from "./RenderOfficerStatus.js";
import type { RevewPoliceFormAndRequestPolicePresenceV2PageState } from "./ReviewPoliceFormAndRequestPresencePageV2.js";
import {
  RequestAndRecordPolicePresenceV2Modals,
  ReviewPoliceFormAndRequestPresencePageV2,
} from "./ReviewPoliceFormAndRequestPresencePageV2.js";
import { api, apiHooks } from "~/api.js";
import type { MenuOptionsType } from "~/components/design-system/index.js";
import {
  Banner,
  BannerList,
  Button,
  ButtonIcon,
  FormTitle,
  MenuFlyout,
  renderErrorToast,
} from "~/components/design-system/index.js";
import { FormFooterSection } from "~/components/form/FormFooter.js";
import { FormCategoryRow } from "~/components/FormCategoryRow.js";
import { PatientBanner } from "~/components/layout/index.js";
import { Container, ExpandMore } from "~/components/layout/styleWrappers.js";
import { LoggedInUserContext, ManagedLocationsContext } from "~/Contexts.js";
import { useExternalPatientLinks } from "~/hooks/ExternalPatientLink/useExternalPatientLinks.js";
import { userCanEditMhaStatus } from "~/pages/EditMhaStatus/userCanEditMhaStatus.js";
import { PatientReconciliationSearchModal } from "~/pages/PatientSearchV2/PatientReconciliationSearchModal.js";
import { routeFns } from "~/routePaths.js";
import { logErrorToDatadog } from "~/tracing.js";

export function PoliceIncidentFormPage({
  reloadFormContext,
  formContext,
  patientTimeline,
  reloadPatientTimeline,
  isReconciliationRoute,
}: {
  formContext: FormContextData;
  reloadFormContext: () => void;
  patientTimeline: PatientTimelineResponse | null;
  reloadPatientTimeline: () => void;
  isReconciliationRoute: boolean;
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [incidentId, setIncidentId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchIncidentId() {
      try {
        const response = await api.vision.getIncidentIdForFormContext(
          formContext.id,
        );
        if (!cancelled && response.status === 200) {
          setIncidentId(response.data.incidentId);
        }
      } catch {
        if (!cancelled) {
          renderErrorToast({
            message: t("pages.policeIncidentFormPage.failedToFetchIncidentId"),
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void fetchIncidentId();
    return () => {
      cancelled = true;
    };
  }, [formContext.id, t]);

  if (loading) {
    return <CircularProgress />;
  }

  if (!incidentId) {
    return (
      <Banner
        title={t("pages.policeIncidentFormPage.incidentNotFound")}
        bannerType={BannerList.ERROR}
      />
    );
  }

  return (
    <PoliceIncidentFormPageInner
      reloadFormContext={reloadFormContext}
      formContext={formContext}
      patientTimeline={patientTimeline}
      reloadPatientTimeline={reloadPatientTimeline}
      incidentId={incidentId}
      isReconciliationRoute={isReconciliationRoute}
    />
  );
}

function Step2IncidentSectionV2({
  formContext,
  officerRequest,
  reloadFormContext,
  reloadShareableIncidentData,
}: {
  formContext: FormContextData;
  officerRequest: ShareableIncidentDataV1Response;
  reloadFormContext: () => void;
  reloadShareableIncidentData: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const hasRequestedPolicePresence =
    officerRequest?.officerRequest.status ===
    OfficerRequestStatusSchema.enum.requested;

  const officerRequestStatus = officerRequest?.officerRequest?.status;

  const [recordPolicePresencePageState, setRecordPolicePresencePageState] =
    useState<RevewPoliceFormAndRequestPolicePresenceV2PageState>("initial");

  return (
    <>
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          boxShadow: 1,
        }}
      >
        <Box
          sx={{
            p: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              mb: 2,
              gap: { xs: 1.5, sm: 0 },
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "common.blueGray",
                fontWeight: 600,
              }}
            >
              {t(
                "pages.policeIncidentFormPage.ongoingPolicePresenceRequestLog",
              )}
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Button
                label={t("pages.policeIncidentFormPage.update")}
                endIcon={ButtonIcon.edit}
                variant={!hasRequestedPolicePresence ? "contained" : "outlined"}
                color="inherit"
                sx={{
                  backgroundColor: !hasRequestedPolicePresence
                    ? "primary.main"
                    : "transparent",
                  color: !hasRequestedPolicePresence ? "white" : "inherit",
                }}
                onClick={() => {
                  setRecordPolicePresencePageState("arePolicePresent");
                }}
              />
            </Box>
          </Box>

          <Box sx={{ maxWidth: { xs: "100%", sm: "60%" } }}>
            <Typography
              gutterBottom
              sx={{
                color: "common.blueGray",
                fontWeight: 500,
                fontSize: "1rem",
              }}
            >
              {t(
                "pages.policeIncidentFormPage.formTitles.ongoingPolicePresence",
              )}
            </Typography>
          </Box>
          {officerRequestStatus && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
                mt: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <RenderOfficerStatus officerRequest={officerRequest} />
              </Box>
              <Button
                label={t("pages.policeIncidentFormPage.viewDetails")}
                variant="outlined"
                size="small"
                onClick={() => {
                  navigate(
                    routeFns.formContextPageActivityLog(
                      formContext.id,
                      formContext.patientId,
                    ),
                  );
                }}
              />
            </Box>
          )}
        </Box>
        <RequestAndRecordPolicePresenceV2Modals
          officerRequest={officerRequest}
          pageState={recordPolicePresencePageState}
          setPageState={setRecordPolicePresencePageState}
          formContext={formContext}
          reloadFormContext={reloadFormContext}
          reloadShareableIncidentData={reloadShareableIncidentData}
        />
      </Box>
    </>
  );
}

export function PoliceIncidentFormPageInner({
  reloadFormContext,
  formContext,
  patientTimeline,
  reloadPatientTimeline,
  incidentId,
  isReconciliationRoute,
}: {
  formContext: FormContextData;
  reloadFormContext: () => void;
  patientTimeline: PatientTimelineResponse | null;
  reloadPatientTimeline: () => void;
  incidentId: string;
  isReconciliationRoute: boolean;
}) {
  const user = useContext(LoggedInUserContext);
  const managedLocationsContext = useContext(ManagedLocationsContext);
  const [isCreatingForm, setIsCreatingForm] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const policeIncidentCardRef = useRef(null);
  const [policeIncidentActionsIsOpen, setPoliceIncidentActionsIsOpen] =
    useState(false);

  const monitoringFormCardRef = useRef(null);
  const [monitoringFormActionsIsOpen, setMonitoringFormActionsIsOpen] =
    useState(false);

  const s136ExtensionFormCardRef = useRef(null);
  const [s136ExtensionFormActionsIsOpen, setS136ExtensionFormActionsIsOpen] =
    useState(false);

  const s136ExtensionFormInProgressCardRef = useRef(null);
  const [
    s136ExtensionFormInProgressActionsIsOpen,
    setS136ExtensionFormInProgressActionsIsOpen,
  ] = useState(false);

  const [showInProgressForms, setShowInProgressForms] = useState(true);
  const [showNewForms, setShowNewForms] = useState(true);
  const [showCompletedForms, setShowCompletedForms] = useState(true);

  /**
   * Generic function to launch a form (create if needed) and navigate to it.
   * @param formTemplateId - The form template ID (e.g., "mha-136-monitoring", "s136-extension")
   * @param formTemplateVersion - The form template version (e.g., "1.0")
   * @param existingForm - The existing form if one already exists (in progress)
   * @param initialDraftData - Optional initial data to populate the draft with (only used when creating new forms)
   */
  const launchForm = useCallback(
    async (
      formTemplateId: string,
      formTemplateVersion: string,
      existingForm?: { id: string } | null,
      initialDraftData?: Record<string, unknown>,
    ) => {
      if (!isCreatingForm) {
        setIsCreatingForm(true);
        try {
          const formDraftId = v4();
          const formId = existingForm?.id ?? v4();

          // Create the form if it doesn't exist
          if (!existingForm) {
            const createFormResult = await api.forms.create(formId, {
              formContext: { mode: "existing", id: formContext.id },
              formTemplate: {
                id: formTemplateId,
                version: formTemplateVersion,
              },
              patientId: formContext.patientId,
            });
            if (createFormResult.status !== 204) {
              renderErrorToast({
                message: t("pages.policeIncidentFormPage.failedCreateForm"),
              });
              return;
            }
          }

          const draftCreateResult = await api.drafts.create(formDraftId, {
            formId,
          });

          if (draftCreateResult.status === 201) {
            // Pre-populate draft with initial data if provided
            // Drafts are user-scoped, so we always set the data for the new draft
            if (initialDraftData) {
              await api.drafts.updateData(formDraftId, initialDraftData);
            }

            navigate(
              routeFns.formDraftsComplete(formDraftId, formContext.patientId),
              {
                replace: true,
              },
            );
          }
        } catch (e) {
          if (e instanceof Error) {
            logErrorToDatadog(e);
          }
          renderErrorToast({
            message: t("pages.policeIncidentFormPage.failedCreateForm"),
          });
        } finally {
          setIsCreatingForm(false);
        }
      }
    },
    [formContext, isCreatingForm, navigate, t],
  );

  const [pdfViewFormId, setPdfViewFormId] = useState<null | string>(null);

  const [
    { loading, data: shareableIncidentData, response },
    reloadShareableIncidentData,
  ] = apiHooks.vision.synchroniseIncidentForFormContext(
    incidentId,
    formContext.id,
  );

  const officerRequest: ShareableIncidentDataV1Response | undefined | null =
    shareableIncidentData;

  const {
    policeForm,
    monitoringForm,
    monitoringFormCompleted,
    anyMonitoringFormCompleted,
    monitoringFormInProgress,
    completedMonitoringFormIds,
    userDraftId,
    needsRequestPolicePresenceResponse,
    isVisionIncidentParticipant,
    isIncidentInProgress,
    isUserMhaAdmin,
    canFinaliseIncidentContext,
    canMonitoringFormBeWorkedOn,
    incidentType,
    isFinalised,
    mayUploadFormsToExternalSystem,
    policeFormCompleted,
    // S136 Extension form
    s136ExtensionForm,
    myS136ExtensionFormInProgress,
    myS136ExtensionFormCompleted,
    s136ExtensionFormInProgress,
    s136ExtensionFormCompleted,
    s136ExtensionFormDraftId,
    s136ExtensionFormId,
    canS136ExtensionFormBeWorkedOn,
    s136ExtensionFormWasNeverCompleted,
  } = policeIncidentLogic(formContext, officerRequest, user?.user.id);

  // Derive i18next context suffix once for all incident-type-specific translations
  const incidentContext = getIncidentTranslationContext(incidentType);
  const tIncidentOptions = { context: incidentContext };

  // Outcome form per-POS cards (replaces the old single monitoring form card
  // when the outcome form feature is enabled)
  const {
    inProgressElements: outcomeFormInProgressCards,
    newFormElements: outcomeFormNewFormCards,
    completedElements: outcomeFormCompletedCards,
    completedFormIds: outcomeFormCompletedFormIds,
  } = useOutcomeFormInProgressCards(
    formContext,
    isFinalised,
    shareableIncidentData,
    t("pages.policeIncidentFormPage.subtitle", tIncidentOptions),
    (formId) => {
      setPdfViewFormId(formId);
    },
    incidentId,
  );
  // External patient links for RIO upload
  const externalLinks = useExternalPatientLinks({
    patientId: formContext.patientId,
  });

  const uploadToRioExternalLink:
    ExternalPatientLinkEnhanced | null | undefined = useMemo(() => {
    if (!mayUploadFormsToExternalSystem) {
      return null;
    }

    if (externalLinks.externalPatientLinksLoading) {
      return undefined;
    }

    const candidateLinks = (
      externalLinks.externalPatientLinks?.externalLinks ?? []
    ).filter(
      (externalPatientLink) => externalPatientLink.externalSystemType === "rio",
    );

    // The lowest number is the highest priority (i.e. 1 is higher priority than 2)
    const highestPriorityLink = candidateLinks
      .filter((externalPatientLink) => externalPatientLink.canPush)
      .sort(
        (a, b) =>
          a.priorityWithinExternalSystem - b.priorityWithinExternalSystem,
      )[0] as ExternalPatientLinkEnhanced | undefined;

    return highestPriorityLink ?? null;
  }, [
    mayUploadFormsToExternalSystem,
    externalLinks.externalPatientLinks?.externalLinks,
    externalLinks.externalPatientLinksLoading,
  ]);

  // Track upload status for police form
  const [policeFormUploadedToRio, setPoliceFormUploadedToRio] = useState<
    { uploaded: false } | { uploaded: true; date: string }
  >({ uploaded: false });

  const [{ data: policeFormPushEvent }] =
    apiHooks.forms.getExternalPatientFormPushEvent(
      formContext.id,
      policeForm.id,
    );

  const shouldTrackPoliceFormUpload =
    uploadToRioExternalLink !== undefined && uploadToRioExternalLink !== null;

  useEffect(() => {
    if (shouldTrackPoliceFormUpload && policeFormPushEvent?.created) {
      setPoliceFormUploadedToRio({
        uploaded: true,
        date: policeFormPushEvent.created,
      });
    }
  }, [policeFormPushEvent?.created, shouldTrackPoliceFormUpload]);

  // Track upload status for monitoring form
  const [monitoringFormUploadedToRio, setMonitoringFormUploadedToRio] =
    useState<{ uploaded: false } | { uploaded: true; date: string }>({
      uploaded: false,
    });

  const [{ data: monitoringFormPushEvent }] =
    apiHooks.forms.getExternalPatientFormPushEvent(
      formContext.id,
      monitoringForm?.id ?? "",
    );

  useEffect(() => {
    if (
      shouldTrackPoliceFormUpload &&
      monitoringForm &&
      monitoringFormPushEvent?.created
    ) {
      setMonitoringFormUploadedToRio({
        uploaded: true,
        date: monitoringFormPushEvent.created,
      });
    }
  }, [
    monitoringFormPushEvent?.created,
    shouldTrackPoliceFormUpload,
    monitoringForm,
  ]);

  // Track upload status for S136 extension form
  const [s136ExtensionFormUploadedToRio, setS136ExtensionFormUploadedToRio] =
    useState<{ uploaded: false } | { uploaded: true; date: string }>({
      uploaded: false,
    });

  const [{ data: s136ExtensionFormPushEvent }] =
    apiHooks.forms.getExternalPatientFormPushEvent(
      formContext.id,
      s136ExtensionForm?.id ?? "",
    );

  useEffect(() => {
    if (
      shouldTrackPoliceFormUpload &&
      s136ExtensionForm &&
      s136ExtensionFormPushEvent?.created
    ) {
      setS136ExtensionFormUploadedToRio({
        uploaded: true,
        date: s136ExtensionFormPushEvent.created,
      });
    }
  }, [
    s136ExtensionFormPushEvent?.created,
    shouldTrackPoliceFormUpload,
    s136ExtensionForm,
  ]);

  // Resolve eMHA participant via user or team fallback for POS config lookups.
  const currentEmhaParticipant = useMemo(
    () =>
      resolveEmhaParticipant(
        shareableIncidentData?.emhaParticipants ?? [],
        user?.user.id,
        formContext.activeTeamworkWorkItem?.teamId,
      ),
    [
      shareableIncidentData?.emhaParticipants,
      user?.user.id,
      formContext.activeTeamworkWorkItem?.teamId,
    ],
  );

  // Look up the curated place of safety configuration for feature flags
  const curatedPlaceOfSafety = useMemo(() => {
    const curatedPosId =
      currentEmhaParticipant?.placeOfSafety?.eMHACuratedPlaceOfSafetyId;
    if (!curatedPosId) return null;
    return (
      managedLocationsContext.curatedPlacesOfSafety.find(
        (pos) => pos.id === curatedPosId,
      ) ?? null
    );
  }, [
    currentEmhaParticipant?.placeOfSafety?.eMHACuratedPlaceOfSafetyId,
    managedLocationsContext.curatedPlacesOfSafety,
  ]);

  // Place of Safety specific feature flags.
  // When the user has a curated POS, respect its config. When there is no
  // curated POS (normal send / manual POS / no POS assigned), default to
  // true — the user will choose a POS in the selection modal.
  const hasOutcomeFormEnabledForPos = curatedPlaceOfSafety
    ? (curatedPlaceOfSafety.config?.outcomeFormEnabled ?? false)
    : true;
  const hasS136ExtensionFormEnabled =
    curatedPlaceOfSafety?.config?.s136ExtensionFormEnabled ?? false;

  const nhsNumber = patientTimeline?.patient.nhsNumber;

  useEffect(() => {
    if (loading || !response) return;

    switch (response.status) {
      case 204:
      case 200:
        reloadFormContext();
        reloadPatientTimeline();
        break;
      default:
        renderErrorToast({
          message: t("pages.policeIncidentFormPage.failedSynchroniseIncident"),
        });
    }
  }, [loading, response]);

  // These buttons should always be disabled until we build 'edit' request
  // police presence
  const activePos = officerRequest?.placesOfSafety.find((pos) => pos.isActive);

  const uploadMenuOptions = {
    uploadToRioExternalLink: uploadToRioExternalLink ?? null,
    uploadFormPdfViaExternalPatientLink:
      externalLinks.uploadFormPdfViaExternalPatientLink,
    formContext,
  };

  const policeIncidentOptions: MenuOptionsType[] = generateMenuOptions(
    policeForm.id,
    (formId) => {
      setPdfViewFormId(formId);
    },
    incidentId,
    "police-form",
    activePos ? [activePos] : [],
    {
      ...uploadMenuOptions,
      isFormCompleted: policeFormCompleted,
      uploadedToRio: policeFormUploadedToRio,
    },
  );

  const monitoringFormMenuOptions: MenuOptionsType[] = monitoringForm
    ? generateMenuOptions(
        monitoringForm.id,
        (formId) => {
          setPdfViewFormId(formId);
        },
        incidentId,
        "monitoring-form",
        activePos ? [activePos] : [],
        {
          ...uploadMenuOptions,
          isFormCompleted: monitoringFormCompleted,
          uploadedToRio: monitoringFormUploadedToRio,
        },
      )
    : [];

  const s136ExtensionFormMenuOptions: MenuOptionsType[] =
    s136ExtensionFormId && !s136ExtensionFormWasNeverCompleted
      ? generateMenuOptions(
          s136ExtensionFormId,
          (formId) => {
            setPdfViewFormId(formId);
          },
          incidentId,
          "s136-extension-form",
          activePos ? [activePos] : [],
          {
            ...uploadMenuOptions,
            isFormCompleted: s136ExtensionFormCompleted,
            uploadedToRio: s136ExtensionFormUploadedToRio,
          },
        )
      : [];

  const s136ExtensionFormInProgressMenuOptions: MenuOptionsType[] =
    s136ExtensionFormDraftId
      ? [
          {
            name: "Continue",
            link: routeFns.formDraftsComplete(
              s136ExtensionFormDraftId,
              formContext.patientId,
            ),
            icon: "edit",
            disabled: isFinalised,
          },
          ...(s136ExtensionFormId
            ? [
                {
                  name: "Delete",
                  onClick: () => {
                    navigate(
                      routeFns.formContextPageConfirmDeleteDraftDialog(
                        formContext.id,
                        formContext.patientId,
                        { draftFormId: s136ExtensionFormId },
                      ),
                    );
                  },
                  icon: "delete",
                  disabled: false,
                },
              ]
            : []),
        ]
      : [];

  const sessionContextPdsOrgOdsCode =
    user?.user.sessionOrganisationConfiguration?.pdsEndUserOrganisationODSCode;

  const patientReconciliationEnabled =
    shareableIncidentData?.featureFlags?.patientReconciliationEnabled;

  // Helper function to derive PDS org from the current participant's place of safety
  const getPdsOrgForReconciliation = (): {
    odsCode: string;
  } | null => {
    // Use session context ODS code if present and org has PDS enabled
    // as this is more 'correct' in most scenarios
    // than the place of safety derived one
    if (
      sessionContextPdsOrgOdsCode &&
      user?.user.sessionOrganisationConfiguration?.pdsEnabled
    ) {
      return { odsCode: sessionContextPdsOrgOdsCode };
    }

    // Find the current participant's place of safety
    const participantPlaceOfSafety = currentEmhaParticipant?.placeOfSafety;

    // Use the persisted trustId from the participant record if available
    if (participantPlaceOfSafety?.trustId) {
      const trust = managedLocationsContext.trusts.find(
        (t) => t.id === participantPlaceOfSafety.trustId,
      );

      // "Trust not listed" toggle was used — no PDS org from context
      if (trust?.notListed) {
        return null;
      }

      if (trust?.odsCode) {
        return { odsCode: trust.odsCode };
      }
    }

    // Fall back to deriving trust from the curated place of safety
    if (!participantPlaceOfSafety?.eMHACuratedPlaceOfSafetyId) {
      return null;
    }

    const curatedPos = managedLocationsContext.curatedPlacesOfSafety.find(
      (pos) => pos.id === participantPlaceOfSafety.eMHACuratedPlaceOfSafetyId,
    );

    if (!curatedPos?.trustId) {
      return null;
    }

    const trust = managedLocationsContext.trusts.find(
      (t) => t.id === curatedPos.trustId,
    );
    if (!trust?.odsCode || trust?.notListed) {
      return null;
    }

    return {
      odsCode: trust.odsCode,
    };
  };

  // Collect completed form IDs for "Download All" / "Upload All to Rio" bundle
  // Order: police form, outcome forms (per-POS), extension form
  // Outcome form IDs come from the per-POS hook when available, falling back
  // to completed monitoring form IDs from policeIncidentLogic for legacy contexts.
  const completedUploadableFormIds = useMemo(() => {
    const ids: string[] = [];
    if (policeFormCompleted) {
      ids.push(policeForm.id);
    }
    // Prefer per-POS outcome form IDs from the hook (the source of truth for
    // the new multi-POS flow). Fall back to legacy completedMonitoringFormIds
    // when no outcome forms exist (e.g. pre-existing single monitoring form).
    if (outcomeFormCompletedFormIds.length > 0) {
      ids.push(...outcomeFormCompletedFormIds);
    } else {
      ids.push(...completedMonitoringFormIds);
    }
    if (s136ExtensionFormId && s136ExtensionFormCompleted) {
      ids.push(s136ExtensionFormId);
    }
    return ids;
  }, [
    policeForm.id,
    policeFormCompleted,
    outcomeFormCompletedFormIds,
    completedMonitoringFormIds,
    s136ExtensionFormId,
    s136ExtensionFormCompleted,
  ]);

  const needsReview =
    patientTimeline &&
    officerRequest &&
    needsRequestPolicePresenceResponse &&
    isVisionIncidentParticipant &&
    isIncidentInProgress &&
    !isUserMhaAdmin;

  // S136 Extension requires doctor OR section-12 accreditation to sign,
  // or guest users (who self-declare as a medical practitioner on the form)
  const userAccreditationTypes = useMemo(
    () => user?.user.accreditations?.map((a) => a.type) ?? [],
    [user?.user.accreditations],
  );
  const isActiveTeamGuest =
    formContext.activeTeamworkWorkItem?.team.type === "guest";
  const canSignS136Extension =
    isActiveTeamGuest ||
    userAccreditationTypes.includes("doctor") ||
    userAccreditationTypes.includes("section-12");

  // Determine if reconciliation is possible
  const pdsOrgForReconciliation = getPdsOrgForReconciliation();
  const canReconcile = checkCanReconcile({
    patientReconciliationEnabled,
    sessionOrganisationConfiguration:
      user?.user.sessionOrganisationConfiguration,
    patientHasNhsNumber: patientTimeline?.patient.nhsNumber != null,
    hasPdsOrgFromContext: pdsOrgForReconciliation != null,
  });

  // Track whether the user dismissed reconciliation during the review flow
  // so we don't keep re-showing it
  const [reconciliationDismissed, setReconciliationDismissed] = useState(false);

  // If on reconciliation route but can't reconcile, redirect away
  useEffect(() => {
    if (!isReconciliationRoute) return;
    if (loading) return; // Wait for data to load

    if (!canReconcile) {
      // Navigate back to the base form context page
      navigate(
        routeFns.formContextPage(formContext.id, formContext.patientId),
        {
          replace: true,
        },
      );
    }
  }, [
    isReconciliationRoute,
    canReconcile,
    loading,
    formContext.id,
    formContext.patientId,
    navigate,
  ]);

  // Show patient reconciliation modal when on the reconciliation route.
  // This check is placed before the needsReview early-returns so that
  // clicking the NHS number shortcut during the PP review flow opens the
  // reconciliation modal instead of being swallowed by the review page.
  if (isReconciliationRoute && patientTimeline && canReconcile) {
    return (
      <PatientReconciliationSearchModal
        open={true}
        onClose={() => {
          navigate(
            routeFns.formContextPage(formContext.id, formContext.patientId),
            { replace: true },
          );
        }}
        onComplete={({ patientId }) => {
          reloadFormContext();
          reloadPatientTimeline();
          navigate(routeFns.formContextPage(formContext.id, patientId), {
            replace: true,
          });
        }}
        currentPatientId={formContext.patientId}
        formContextId={formContext.id}
        pdsOrg={pdsOrgForReconciliation ?? undefined}
        currentPatient={formContext.patient}
        currentPatientNhsNumber={nhsNumber ?? undefined}
      />
    );
  }

  // Show patient reconciliation modal after the POS review dialog
  // but before the PDF review / police presence flow.
  // This auto-popup is Vision-specific behaviour — only fires when the Vision
  // flag is on. When only the Aspire flag is active, users reconcile via NHS click.
  if (
    needsReview &&
    patientReconciliationEnabled &&
    canReconcile &&
    !reconciliationDismissed
  ) {
    return (
      <PatientReconciliationSearchModal
        open={true}
        onClose={() => {
          setReconciliationDismissed(true);
        }}
        onComplete={() => {
          reloadFormContext();
          reloadPatientTimeline();
        }}
        currentPatientId={formContext.patientId}
        formContextId={formContext.id}
        pdsOrg={pdsOrgForReconciliation ?? undefined}
        currentPatient={formContext.patient}
        currentPatientNhsNumber={nhsNumber ?? undefined}
      />
    );
  }

  // MHAAs are excluded from `needsReview` because they skip the police
  // presence flow, but they still need to see the reconciliation prompt
  // when accepting a S136 incident. MCA MHAAs are already handled via
  // the reconciliation route (Path A above), so this only targets S136.
  if (
    isUserMhaAdmin &&
    incidentType === "s136" &&
    patientTimeline &&
    officerRequest &&
    isVisionIncidentParticipant &&
    isIncidentInProgress &&
    patientReconciliationEnabled &&
    canReconcile &&
    !reconciliationDismissed &&
    !isReconciliationRoute
  ) {
    return (
      <PatientReconciliationSearchModal
        open={true}
        onClose={() => {
          setReconciliationDismissed(true);
        }}
        onComplete={() => {
          reloadFormContext();
          reloadPatientTimeline();
        }}
        currentPatientId={formContext.patientId}
        formContextId={formContext.id}
        pdsOrg={pdsOrgForReconciliation ?? undefined}
        currentPatient={formContext.patient}
        currentPatientNhsNumber={nhsNumber ?? undefined}
      />
    );
  }

  // Helper to build the onReconcileClick callback for PatientBanner
  const reconcileClickHandler = canReconcile
    ? () => {
        navigate(
          routeFns.formContextPagePatientReconciliation(
            formContext.id,
            formContext.patientId,
          ),
        );
      }
    : undefined;

  // Short-circuit and force the user through the review / request flow
  // when the user has accessed through the share code and has not yet
  // specified if they want police presence or not
  // DON'T do this if:
  //  - the user is an MHA admin
  //  - the incident is ended
  //  - a monitoring form has been completed
  if (needsReview) {
    return (
      <ReviewPoliceFormAndRequestPresencePageV2
        officerRequest={officerRequest}
        formContext={formContext}
        reloadFormContext={reloadFormContext}
        reloadShareableIncidentData={reloadShareableIncidentData}
        patientTimeline={patientTimeline}
        reloadPatientTimeline={reloadPatientTimeline}
        onReconcileClick={reconcileClickHandler}
        onFlowComplete={() => {
          // Navigate to the base form page after review flow completes
          navigate(
            routeFns.formContextPage(formContext.id, formContext.patientId),
            { replace: true },
          );
        }}
      />
    );
  }

  const showEditMhaStatus = userCanEditMhaStatus(user?.user);

  const showFinaliseConfirmation = () => {
    navigate(
      routeFns.formContextPageConfirmFinaliseDialog(
        formContext.id,
        formContext.patientId,
        {
          itemName: t(
            "pages.policeIncidentFormPage.itemName",
            tIncidentOptions,
          ),
        },
      ),
    );
  };

  const showMhaStatusConfirmation = () => {
    navigate(
      routeFns.formContextPageEditMhaStatusDialog(
        formContext.id,
        formContext.patientId,
        {
          itemName: t(
            "pages.policeIncidentFormPage.itemName",
            tIncidentOptions,
          ),
        },
      ),
    );
  };

  const showMonitoringFormStep =
    incidentType === "s136" &&
    (hasOutcomeFormEnabledForPos ||
      monitoringFormInProgress ||
      monitoringFormCompleted);

  // Only show if the user's own POS has the form enabled,
  // or if the user's own work item already has the form (backward compat).
  // Context-wide forms started by other participants at a different POS
  // also make this step visible so every team can start their own extension.
  const showS136ExtensionFormStep =
    incidentType === "s136" &&
    (hasS136ExtensionFormEnabled ||
      s136ExtensionFormInProgress ||
      s136ExtensionFormCompleted ||
      myS136ExtensionFormInProgress ||
      myS136ExtensionFormCompleted);

  return loading ? (
    <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
      <CircularProgress />
    </Box>
  ) : (
    <>
      <Box
        data-testid="form-context:police-incident"
        data-formcontextid={formContext.id}
      >
        <externalLinks.Component />
        {pdfViewFormId && (
          <FormContextPdfViewer
            hideUploadedFormInfo
            inModal
            forms={formContext.forms
              .filter((f) => {
                // Include forms that have a PDF or at least one signature.
                // Exclude forms that were force-finalised while still
                // in-progress (e.g. outcome forms when the incident was
                // finalised) — they have no PDF and no signatures, so
                // viewing them causes an infinite loading spinner.
                const latest = f.versions[f.latestVersion - 1];
                const isSigned = (latest?.signatures.length ?? 0) > 0;
                return (
                  (formIsCompleted(f) || isSigned) &&
                  (!!latest?.pdfUri || isSigned)
                );
              })
              .sort((f1, f2) => (f1.updated < f2.updated ? 1 : -1))}
            setFormId={setPdfViewFormId}
            formId={pdfViewFormId}
          />
        )}

        {patientTimeline && (
          <Box sx={{ mb: 3 }}>
            <PatientBanner
              patient={formContext.patient}
              nhsNumber={nhsNumber ?? undefined}
              disableSticky
              isHorizontalLineHidden
              patientTimeline={patientTimeline}
              reloadPatientTimeline={reloadPatientTimeline}
              onReconcileClick={reconcileClickHandler}
            />
          </Box>
        )}

        {canFinaliseIncidentContext && (
          <Box sx={{ mb: 2 }}>
            <Banner
              title={t("pages.policeIncidentFormPage.markScrutinyComplete", {
                itemName: t(
                  "pages.policeIncidentFormPage.itemName",
                  tIncidentOptions,
                ),
              })}
              rightButtonLabel={t("buttonLabels.complete")}
              rightButtonTestId="mark-scrutiny-complete-button"
              onRightButton={
                showEditMhaStatus
                  ? showMhaStatusConfirmation
                  : showFinaliseConfirmation
              }
              bannerType={BannerList.INFO}
            />
          </Box>
        )}

        <WorkItemStatusCard
          formContext={formContext}
          reloadFormContext={reloadFormContext}
          user={user?.user!}
          suppressTeamClaimBanner
        />

        <Box sx={{ mb: 3, ml: 2 }}>
          <FormTitle
            hasContainerMarginBottom={false}
            hasTitleBottomMargin={false}
            useReducedTopPadding={true}
            titleText={t(
              "pages.policeIncidentFormPage.title",
              tIncidentOptions,
            )}
          />
        </Box>

        {/* IN-PROGRESS FORMS SECTION */}
        {(() => {
          const inProgressItems: ReactElement[] = [];
          // Track actual form count separately — the police presence section
          // is rendered inside this section but is not a form, so it must not
          // inflate the "In Progress Forms (N)" count.
          let inProgressFormCount = 0;

          // Outcome forms in progress (per-POS cards)
          if (showMonitoringFormStep) {
            inProgressItems.push(...outcomeFormInProgressCards);
            inProgressFormCount += outcomeFormInProgressCards.length;
          }

          // Legacy: single monitoring form in progress (fallback when no POS-linked outcome forms exist)
          if (
            showMonitoringFormStep &&
            monitoringFormInProgress &&
            userDraftId &&
            outcomeFormInProgressCards.length === 0
          ) {
            inProgressFormCount += 1;
            inProgressItems.push(
              <Box
                key="in-progress-monitoring"
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  mb: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", sm: "center" },
                    gap: 2,
                    p: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: "common.blueGray",
                      fontWeight: 600,
                    }}
                  >
                    {t(
                      "pages.policeIncidentFormPage.formTitles.sectionOutcome",
                    )}
                  </Typography>
                  <Button
                    label={t("pages.policeIncidentFormPage.continue")}
                    disabled={isFinalised}
                    onClick={() => {
                      navigate(
                        routeFns.formDraftsComplete(
                          userDraftId,
                          formContext.patientId,
                        ),
                      );
                    }}
                  />
                </Box>
              </Box>,
            );
          }

          // S136 Extension form in progress (user-scoped: each team sees their own draft)
          if (
            showS136ExtensionFormStep &&
            myS136ExtensionFormInProgress &&
            s136ExtensionFormDraftId
          ) {
            inProgressFormCount += 1;
            inProgressItems.push(
              <Box
                key="in-progress-s136-extension"
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  mb: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", sm: "center" },
                    gap: 2,
                    p: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: "common.blueGray",
                      fontWeight: 600,
                    }}
                  >
                    {t("pages.policeIncidentFormPage.formTitles.s136Extension")}
                  </Typography>
                  {!!s136ExtensionFormInProgressMenuOptions.length && (
                    <Box
                      ref={s136ExtensionFormInProgressCardRef}
                      onClick={() => {
                        setS136ExtensionFormInProgressActionsIsOpen(
                          !s136ExtensionFormInProgressActionsIsOpen,
                        );
                      }}
                      sx={{ cursor: "pointer", ml: 1 }}
                    >
                      <MenuFlyout
                        options={s136ExtensionFormInProgressMenuOptions}
                        cardRef={s136ExtensionFormInProgressCardRef}
                        isOpen={s136ExtensionFormInProgressActionsIsOpen}
                        onClose={() => {
                          setS136ExtensionFormInProgressActionsIsOpen(false);
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </Box>,
            );
          }

          // Police presence request (in-progress when incident is ongoing).
          // Once any outcome form has been signed the police presence step
          // is permanently hidden — it must not reappear when further
          // outcome forms are started later.
          if (
            isIncidentInProgress &&
            isVisionIncidentParticipant &&
            !anyMonitoringFormCompleted
          ) {
            inProgressItems.push(
              <Fragment key="in-progress-police-presence">
                <Step2IncidentSectionV2
                  formContext={formContext}
                  officerRequest={officerRequest!}
                  reloadFormContext={reloadFormContext}
                  reloadShareableIncidentData={reloadShareableIncidentData}
                />
              </Fragment>,
            );
          }

          return inProgressItems.length > 0 ? (
            <Container addMarginBottom>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <FormTitle
                  subtitleText={t(
                    "pages.policeIncidentFormPage.sectionTitleInProgress",
                    {
                      count: inProgressFormCount,
                    },
                  )}
                  hasContainerMarginBottom={false}
                  subTitleTextFontSize={2}
                />
                <ExpandMore
                  role="button"
                  isExpanded={showInProgressForms}
                  onClick={() => {
                    setShowInProgressForms(!showInProgressForms);
                  }}
                  aria-expanded={showInProgressForms}
                  aria-label={t("components.cardExtended.expandMoreLabel")}
                >
                  <ExpandMoreIcon sx={{ color: "primary.main" }} />
                </ExpandMore>
              </Box>
              {showInProgressForms && inProgressItems}
            </Container>
          ) : null;
        })()}

        {/* NEW FORMS SECTION */}
        {(() => {
          const newFormItems: ReactElement[] = [];

          // Outcome form per-POS "Add Outcome Form" button
          if (showMonitoringFormStep) {
            newFormItems.push(...outcomeFormNewFormCards);
          }

          // Legacy: single monitoring form "Start Form" (only shown when
          // no POS-linked outcome forms exist yet and no "Add" card is showing)
          if (
            showMonitoringFormStep &&
            canMonitoringFormBeWorkedOn &&
            !monitoringFormInProgress &&
            !monitoringFormCompleted &&
            outcomeFormNewFormCards.length === 0
          ) {
            newFormItems.push(
              <FormCategoryRow
                key="new-monitoring-form"
                buttonTestId="start-new-form-button--monitoring"
                title={t(
                  "pages.policeIncidentFormPage.formTitles.sectionOutcome",
                )}
                subtitle={t(
                  "pages.policeIncidentFormPage.subtitle",
                  tIncidentOptions,
                )}
                onLaunchForm={() => {
                  void launchForm("mha-136-monitoring", "1.0", monitoringForm);
                }}
                launchFormLabel={t("pages.formPage.startForm")}
              />,
            );
          }

          // S136 Extension form — can be started but hasn't been yet (user-scoped)
          if (
            showS136ExtensionFormStep &&
            canS136ExtensionFormBeWorkedOn &&
            !myS136ExtensionFormInProgress &&
            !myS136ExtensionFormCompleted
          ) {
            if (canSignS136Extension) {
              // User has the required accreditations — show "Start Form"
              newFormItems.push(
                <FormCategoryRow
                  key="new-s136-extension-form"
                  buttonTestId="start-new-form-button--s136-extension"
                  title={t(
                    "pages.policeIncidentFormPage.formTitles.s136Extension",
                  )}
                  subtitle={t(
                    "pages.policeIncidentFormPage.subtitle",
                    tIncidentOptions,
                  )}
                  onLaunchForm={() => {
                    // Build default context with patient and user details
                    const isGuest =
                      formContext.activeTeamworkWorkItem?.team.type === "guest";
                    const defaultContext =
                      user?.user && patientTimeline
                        ? buildDefaultContext(
                            patientTimeline.patient,
                            user.user,
                            isGuest,
                          )
                        : {};

                    // Pre-populate with place of safety from participant if available
                    const placeOfSafetyData =
                      currentEmhaParticipant?.placeOfSafety
                        ? {
                            placeOfSafety: {
                              name: currentEmhaParticipant.placeOfSafety.name,
                              address:
                                currentEmhaParticipant.placeOfSafety.address,
                              postalCode:
                                currentEmhaParticipant.placeOfSafety.postalCode,
                              isConfirmed: false,
                              placeOfSafetyId:
                                currentEmhaParticipant.placeOfSafety
                                  .eMHACuratedPlaceOfSafetyId ?? undefined,
                              trustId:
                                currentEmhaParticipant.placeOfSafety.trustId ??
                                undefined,
                              trustName:
                                managedLocationsContext.trusts.find(
                                  (t) =>
                                    t.id ===
                                    currentEmhaParticipant.placeOfSafety
                                      ?.trustId,
                                )?.name ?? undefined,
                            },
                          }
                        : {};

                    // Pre-populate the detention start date/time from the
                    // patient's Section 136 status to keep it consistent with
                    // the recorded MHA status. The field stays editable.
                    const detentionStartData = buildDetentionStartPrefill(
                      patientTimeline?.patientMhaStatus?.state.mhaStatus,
                    );

                    const initialData = {
                      ...defaultContext,
                      ...placeOfSafetyData,
                      ...detentionStartData,
                    };

                    // Prefer an existing in-progress extension form
                    // (started by any participant) so a new draft is created
                    // on that form instead of spawning a duplicate form.
                    const existingExtensionForm = s136ExtensionFormId
                      ? { id: s136ExtensionFormId }
                      : null;

                    void launchForm(
                      "s136-extension",
                      "1.0",
                      existingExtensionForm,
                      initialData,
                    );
                  }}
                  launchFormLabel={t("pages.formPage.startForm")}
                />,
              );
            } else {
              // User does NOT have the required accreditations — show "Request Form"
              newFormItems.push(
                <FormCategoryRow
                  key="request-s136-extension-form"
                  buttonTestId="request-form-button--s136-extension"
                  title={t(
                    "pages.policeIncidentFormPage.formTitles.s136Extension",
                  )}
                  subtitle={t(
                    "pages.policeIncidentFormPage.subtitle",
                    tIncidentOptions,
                  )}
                  onLaunchForm={() => {
                    navigate(
                      routeFns.formContextPageSendRequest(
                        formContext.id,
                        formContext.patientId,
                        {
                          requestType: "share",
                          title: t(
                            "pages.policeIncidentFormPage.inviteToExtendS136",
                          ),
                        },
                      ),
                    );
                  }}
                  launchFormLabel={t("pages.formPage.requestForm")}
                />,
              );
            }
          }

          return newFormItems.length > 0 ? (
            <Container addMarginBottom>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <FormTitle
                  subtitleText={t(
                    "pages.policeIncidentFormPage.sectionTitleForms",
                  )}
                  hasContainerMarginBottom={false}
                  subTitleTextFontSize={2}
                />
                <ExpandMore
                  role="button"
                  isExpanded={showNewForms}
                  onClick={() => {
                    setShowNewForms(!showNewForms);
                  }}
                  aria-expanded={showNewForms}
                  aria-label={t("components.cardExtended.expandMoreLabel")}
                >
                  <ExpandMoreIcon sx={{ color: "primary.main" }} />
                </ExpandMore>
              </Box>
              {showNewForms && <Box sx={{ mt: 1 }}>{newFormItems}</Box>}
            </Container>
          ) : null;
        })()}

        {/* COMPLETED FORMS SECTION */}
        {(() => {
          const completedItems: ReactElement[] = [];

          // Police form (always completed — arrives from Vision/police)
          if (policeFormCompleted) {
            completedItems.push(
              <Box
                key="completed-police-form"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 2,
                  backgroundColor: "#FCFCFC",
                  borderRadius: (t) => t.spacing(0.75),
                }}
              >
                <Box
                  sx={{
                    borderLeft: (t) =>
                      `${t.spacing(0.4)} solid ${t.palette.primary.main}`,
                    pl: 1.2,
                  }}
                >
                  <Typography sx={{ fontSize: 22, fontWeight: 400 }}>
                    {t(
                      "pages.policeIncidentFormPage.formTitles.policeIncident",
                      tIncidentOptions,
                    )}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: "primary.hint" }}>
                    {t(
                      "pages.policeIncidentFormPage.subtitle",
                      tIncidentOptions,
                    )}
                  </Typography>
                </Box>
                {!!policeIncidentOptions.length && (
                  <Box
                    ref={policeIncidentCardRef}
                    onClick={() => {
                      setPoliceIncidentActionsIsOpen(
                        !policeIncidentActionsIsOpen,
                      );
                    }}
                    sx={{ cursor: "pointer", ml: 1 }}
                  >
                    <MenuFlyout
                      cardRef={policeIncidentCardRef}
                      options={policeIncidentOptions}
                      isOpen={policeIncidentActionsIsOpen}
                      onClose={() => {
                        setPoliceIncidentActionsIsOpen(false);
                      }}
                    />
                  </Box>
                )}
              </Box>,
            );
          }

          // Completed outcome forms (per-POS cards)
          if (showMonitoringFormStep) {
            completedItems.push(...outcomeFormCompletedCards);
          }

          // Legacy: Monitoring form completed (fallback when no POS-linked
          // completed outcome forms exist)
          if (
            showMonitoringFormStep &&
            monitoringFormCompleted &&
            outcomeFormCompletedCards.length === 0
          ) {
            completedItems.push(
              <Box
                key="completed-monitoring-form"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 2,
                  backgroundColor: "#FCFCFC",
                  borderRadius: (t) => t.spacing(0.75),
                }}
              >
                <Box
                  sx={{
                    borderLeft: (t) =>
                      `${t.spacing(0.4)} solid ${t.palette.primary.main}`,
                    pl: 1.2,
                  }}
                >
                  <Typography sx={{ fontSize: 22, fontWeight: 400 }}>
                    {t(
                      "pages.policeIncidentFormPage.formTitles.sectionOutcome",
                    )}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: "primary.hint" }}>
                    {t(
                      "pages.policeIncidentFormPage.subtitle",
                      tIncidentOptions,
                    )}
                  </Typography>
                </Box>
                {!!monitoringFormMenuOptions.length && (
                  <Box
                    ref={monitoringFormCardRef}
                    onClick={() => {
                      setMonitoringFormActionsIsOpen(
                        !monitoringFormActionsIsOpen,
                      );
                    }}
                    sx={{ cursor: "pointer", ml: 1 }}
                  >
                    <MenuFlyout
                      options={monitoringFormMenuOptions}
                      cardRef={monitoringFormCardRef}
                      isOpen={monitoringFormActionsIsOpen}
                      onClose={() => {
                        setMonitoringFormActionsIsOpen(false);
                      }}
                    />
                  </Box>
                )}
              </Box>,
            );
          }

          // S136 Extension form completed
          if (showS136ExtensionFormStep && s136ExtensionFormCompleted) {
            completedItems.push(
              <Box
                key="completed-s136-extension-form"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 2,
                  backgroundColor: "#FCFCFC",
                  borderRadius: (t) => t.spacing(0.75),
                }}
              >
                <Box
                  sx={{
                    borderLeft: (t) =>
                      `${t.spacing(0.4)} solid ${t.palette.primary.main}`,
                    pl: 1.2,
                  }}
                >
                  <Typography sx={{ fontSize: 22, fontWeight: 400 }}>
                    {t("pages.policeIncidentFormPage.formTitles.s136Extension")}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: "primary.hint" }}>
                    {t(
                      "pages.policeIncidentFormPage.subtitle",
                      tIncidentOptions,
                    )}
                  </Typography>
                </Box>
                {!!s136ExtensionFormMenuOptions.length && (
                  <Box
                    ref={s136ExtensionFormCardRef}
                    onClick={() => {
                      setS136ExtensionFormActionsIsOpen(
                        !s136ExtensionFormActionsIsOpen,
                      );
                    }}
                    sx={{ cursor: "pointer", ml: 1 }}
                  >
                    <MenuFlyout
                      options={s136ExtensionFormMenuOptions}
                      cardRef={s136ExtensionFormCardRef}
                      isOpen={s136ExtensionFormActionsIsOpen}
                      onClose={() => {
                        setS136ExtensionFormActionsIsOpen(false);
                      }}
                    />
                  </Box>
                )}
              </Box>,
            );
          }

          return completedItems.length > 0 ? (
            <Container addMarginBottom>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <FormTitle
                  subtitleText={t(
                    "pages.policeIncidentFormPage.sectionTitleCompleted",
                    {
                      count: completedItems.length,
                    },
                  )}
                  hasContainerMarginBottom={false}
                  subTitleTextFontSize={2}
                />
                {completedUploadableFormIds.length > 1 && (
                  <Button
                    variant={"outlined"}
                    color={"primary"}
                    label={t("pages.policeIncidentFormPage.downloadAll")}
                    endIcon={ButtonIcon.download}
                    sx={{ ml: 2, height: 40 }}
                    onClick={() => {
                      const onDownloadAll = async () => {
                        const firstFormId = completedUploadableFormIds[0];
                        if (firstFormId === undefined) return;
                        const result = await api.forms.getPdf(
                          firstFormId,
                          false,
                          completedUploadableFormIds.slice(1),
                        );
                        if (result.status === 200 && result.data) {
                          const dataUri = `data:application/pdf;base64,${result.data}`;
                          triggerDownload(dataUri, "forms.pdf");
                        } else {
                          renderErrorToast({
                            message: t(
                              "pages.policeIncidentFormPage.failedPdfDownload",
                            ),
                          });
                        }
                      };
                      void onDownloadAll();
                    }}
                  />
                )}
                {mayUploadFormsToExternalSystem &&
                  uploadToRioExternalLink &&
                  completedUploadableFormIds.length > 1 && (
                    <Button
                      variant={"outlined"}
                      color={"primary"}
                      label={t("pages.policeIncidentFormPage.uploadAllToRio")}
                      endIcon={ButtonIcon.rioUpload}
                      sx={{ ml: 2, height: 40 }}
                      onClick={() => {
                        const onUploadAll = async () => {
                          await externalLinks.uploadFormPdfViaExternalPatientLink(
                            uploadToRioExternalLink,
                            completedUploadableFormIds,
                            formContext,
                          );
                        };
                        void onUploadAll();
                      }}
                    />
                  )}
                <ExpandMore
                  role="button"
                  isExpanded={showCompletedForms}
                  onClick={() => {
                    setShowCompletedForms(!showCompletedForms);
                  }}
                  aria-expanded={showCompletedForms}
                  aria-label={t("components.cardExtended.expandMoreLabel")}
                >
                  <ExpandMoreIcon sx={{ color: "primary.main" }} />
                </ExpandMore>
              </Box>
              {showCompletedForms && completedItems}
            </Container>
          ) : null;
        })()}

        {/* Participants List */}
        <ParticipantsList formContext={formContext} />

        <FormFooterSection
          hideSubmit={!formContext.activeTeamworkWorkItem}
          isSticky={true}
          onSave={() => {
            navigate(
              routeFns.formContextPageSendRequest(
                formContext.id,
                formContext.patientId,
                {},
              ),
            );
          }}
          saveLabel={t("buttonLabels.send")}
          discardLabel={t("buttonLabels.activityLog")}
          onCancel={() => {
            navigate(
              routeFns.formContextPageActivityLog(
                formContext.id,
                formContext.patientId,
              ),
            );
          }}
        />
      </Box>
    </>
  );
}
